import React, { useContext } from 'react'
import dynamic from 'next/dynamic'
import { Hide, Box, Divider, Text } from 'pcln-design-system'
import { AppDownload } from '@pcln/vip'
import { useContentful, ToutType } from '@pcln/contentful-components'
import MerchModuleWrapper from '../../MerchModuleWrapper'
import AdBanner from '../../AdBanner'
import WhyPricelineBanner from '../../WhyPricelineBanner'
import PopularDestinations from '../../PopularDestinations'
import CreditCard from '../../CreditCard/CreditCard'
import { ANALYTICS_CATEGORY_HOME } from '../../../constants/analytics'
import DISNEY_CONTENT from '../../../constants/promotions/disney'
import BannerContent from '../../BannerContent'
import EmailSignUp from '../../EmailSignUp'
import PromoFeatureBox from '../../PromoFeatureBox'
import PackagesCarousel from '../../PackagesCarousel'
import FindMyTripBanner from '../../Hotels/FindMyTripBanner'
import RecentSearchCards from '../../RecentSearchCards'
import CrossSell from '../../CrossSell'
import useMostRecentFutureBooking from '../../CustomHooks/useMostRecentFutureBooking'
import useExpressDealModal from '../../CustomHooks/useExpressDealModal'
import ExpressDealModal from '../../Hotels/ExpressDeal/ExpressDealModal'
import ConditionalRender, {
  Experiment,
  Original
} from '../../ConditionalRender/ConditionalRender'
import MarketingTout from '../../Promos/MarketingTout/MarketingTout'
import BootstrapDataContext from '../../../context/BootstrapDataContext'
import PhoneEntry from '../../PhoneFormEntry/PhoneEntry'
import useSeti from '../../CustomHooks/useSeti'
import useCurrentBooking from '../../CustomHooks/useCurrentBooking'

const ExtendStay = dynamic(() => import('../../ExtendStay/ExtendStay'))
const expressDealQSKey = 'expressdeal'
const expressDealQSValue = 'y'

export default function HomeContent() {
  const mostRecentFutureBooking = useMostRecentFutureBooking()
  const extendData = useCurrentBooking()

  const { isOpen } = useExpressDealModal(expressDealQSKey, expressDealQSValue)
  const {
    webstats: { clientGUID },
    locale: { ipAddr },
    referral
  } = useContext(BootstrapDataContext)
  const isAppDownloadPhoneFormSeti =
    useSeti('HP_APP_DOWNLOAD_PHONE_FORM') === 'VARIANT'

  const AdBannerContent = () => {
    return (
      <>
        <AdBanner>
          <div id="div-gpt-ad-1614716188275-0" />
        </AdBanner>
        <Divider borderColor="border.base" my={0} />
      </>
    )
  }

  const CrossSellContent = () => {
    return (
      <>
        {mostRecentFutureBooking ? (
          <Box my={3} mx="auto" p={3} maxWidth="1280px">
            <CrossSell booking={mostRecentFutureBooking} />
          </Box>
        ) : (
          <PromoFeatureBox />
        )}
      </>
    )
  }

  const promoCollection: any = useContentful('promoCollection')
  const promoItems: ToutType[] = promoCollection?.items || []
  function renderMerchModules(merchModules: ToutType[]) {
    return merchModules.length > 0 ? (
      <>
        {merchModules.map((merchModule: ToutType) => {
          return (
            <React.Fragment key={merchModule?.headline}>
              <MerchModuleWrapper {...merchModule} />
              <Divider m={0} />
            </React.Fragment>
          )
        })}
      </>
    ) : (
      // Backup is just the existing Disney content for now
      <BannerContent banners={[DISNEY_CONTENT]} />
    )
  }

  return (
    <>
      <ExpressDealModal
        isModalOpen={isOpen}
        gaCategory={ANALYTICS_CATEGORY_HOME}
        qsKey={expressDealQSKey}
        qsValue={expressDealQSValue}
      />
      <ConditionalRender
        experimentCode="HP_FMT"
        variantValue="VARIANT"
        experiment={<FindMyTripBanner isMobile={false} />}
      />
      <RecentSearchCards />
      <Hide md>
        <ConditionalRender
          experimentCode="HP_EXTEND_YOUR_TRIP_PROMO"
          triggerExperiment={() => !!extendData}
        >
          <Original>
            <CrossSellContent />
          </Original>
          <Experiment>
            {extendData ? (
              <Box my={3} mx="auto" p={3} maxWidth="1280px">
                <ExtendStay {...extendData} />
              </Box>
            ) : (
              <CrossSellContent />
            )}
          </Experiment>
        </ConditionalRender>
      </Hide>
      <Divider borderColor="border.base" my={0} />
      <WhyPricelineBanner />
      <Divider borderColor="border.base" my={0} />
      <MarketingTout />
      <PackagesCarousel heading="Don't miss these hotel + flight deals" />
      <Divider borderColor="border.base" my={0} />
      <AppDownload
        ctaArea={
          isAppDownloadPhoneFormSeti ? (
            <PhoneEntry {...{ clientGUID, ipAddr, referral }} />
          ) : undefined
        }
      />
      <Divider borderColor="border.base" my={0} />
      <EmailSignUp sourceId="LANDING_HOME" category={ANALYTICS_CATEGORY_HOME}>
        <Text color="text" fontWeight="bold" fontSize={3}>
          Sign up for Exclusive Email-only Coupons
        </Text>
        <Text color="text.light" fontSize={1} fontWeight="bold" pt={2}>
          Exclusive access to coupons, special offers and promotions.
        </Text>
      </EmailSignUp>
      <Divider borderColor="border.base" my={0} />
      <PopularDestinations />
      <Divider borderColor="border.base" my={0} />
      {renderMerchModules(promoItems)}
      <CreditCard />
      <ConditionalRender
        experimentCode="HP_728X90"
        variantValue="VARIANT"
        experiment={<AdBannerContent />}
      />
    </>
  )
}
